import {inject, Injectable} from "@angular/core"
import {FilesService} from "@app/common/services/files/files.service"
import {PdfGenerationService} from "@app/common/services/pdf-generation/pdf-generation.service"
import {StlExportService} from "@app/common/services/stl-export/stl-export.service"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {ThreeSceneManagerService} from "@app/template-editor/services/three-scene-manager.service"
import {Parameters, TemplateGraph} from "@cm/template-nodes"
import {Observable, Subject} from "rxjs"
import {GltfExportService} from "@app/common/services/gltf-export/gltf-export.service"

@Injectable({
    providedIn: "root",
})
export class ActionMenuService {
    private readonly stlExportService = inject(StlExportService)
    private readonly pdfService = inject(PdfGenerationService)
    protected readonly gltfExportService = inject(GltfExportService)

    private creatingStlSubject = new Subject<boolean>()
    creatingStl$ = this.creatingStlSubject.asObservable()

    private creatingGltfSubject = new Subject<boolean>()
    creatingGltf$ = this.creatingGltfSubject.asObservable()

    private toggleFullscreenSubject = new Subject<void>()
    toggleFullscreen$ = this.toggleFullscreenSubject.asObservable()

    private toggleDimensionGuidesSubject = new Subject<void>()
    toggleDimensionGuides$ = this.toggleDimensionGuidesSubject.asObservable()

    private captureSnapshotSubject = new Subject<void>()
    captureSnapshot$ = this.captureSnapshotSubject.asObservable()

    private pdfDownloadRequestedSubject = new Subject<void>()
    pdfDownloadRequested$ = this.pdfDownloadRequestedSubject.asObservable()
    emitPdfDownloadRequested(): void {
        this.pdfDownloadRequestedSubject.next()
    }

    private pdfDownloadFinishedSubject = new Subject<void>()

    emitPdfDownloadFinished(): void {
        this.pdfDownloadFinishedSubject.next()
    }

    pdfDownloadFinished$(): Observable<void> {
        return this.pdfDownloadFinishedSubject.asObservable()
    }

    toggleFullscreen(): void {
        this.toggleFullscreenSubject.next()
    }

    toggleDimensionGuides(): void {
        this.toggleDimensionGuidesSubject.next()
    }

    async downloadStl(
        templateGraph: TemplateGraph,
        parameters: Parameters,
        sceneOptions:
            | {
                  maxSubdivisionLevel: number
                  maxSubdivisionLevelOnMobile?: number
              }
            | undefined,
        sceneManagerService: SceneManagerService,
    ): Promise<void> {
        this.creatingStlSubject.next(true)
        const zippedStls = await this.stlExportService.exportStlFiles(templateGraph, parameters, sceneOptions, sceneManagerService)
        FilesService.downloadFile("stl-export.zip", zippedStls)
        this.creatingStlSubject.next(false)
    }

    async downloadGltf(
        templateGraph: TemplateGraph,
        parameters: Parameters,
        sceneOptions:
            | {
                  maxSubdivisionLevel: number
                  maxSubdivisionLevelOnMobile?: number
              }
            | undefined,
        threeSceneManagerService: ThreeSceneManagerService,
    ): Promise<void> {
        this.creatingGltfSubject.next(true)
        const binaryGltf = await this.gltfExportService.exportGltfFile(templateGraph, parameters, sceneOptions, threeSceneManagerService, "web")
        FilesService.downloadFile("glb-export.glb", new Blob([binaryGltf as Uint8Array<ArrayBuffer>]))
        this.creatingGltfSubject.next(false)
    }

    captureSnapshot() {
        this.captureSnapshotSubject.next()
    }
}
